import { backend_api } from '@/utils/api'
import bus from '@/utils/event_bus'
import { defineStore } from 'pinia'
import { useAppStore } from "@/stores/app"
import { useRefurbishmentStore } from "@/stores/refurb"
import { useShipmentsStore } from "@/stores/shipments"
import { usePartnumbersStore } from "@/stores/partnumbers"
import { useExternalShipmentsStore } from "@/stores/external_shipments"
import { useContainersStore } from "@/stores/containers"
import base from "@/stores/base"

function determineUserLanguage(auth) {
    if (auth.multilang && auth.language) {
        return auth.language || "en"
    } else
        return auth.company.meta.language || "en"
}


export const useSessionStore = defineStore('session', {
    state: () => ({
        access_token: localStorage.getItem('access_token'),
        refresh_token: localStorage.getItem('refresh_token'),
        authenticated: !!localStorage.getItem('access_token'),
        user_object: {
            _flag: "not-initialized",
            username: "",
            auth: {}
        },

        // THIS COMES FROM THE SIM_AUTH DJANGO APP
        companies: [],
        users: [],
        holdings: [],
    }),
    getters: {
        iam_admin: state => state.user_object.auth?.company_admin,
        has_admin_privileges: state => state.user_object.auth?.company_admin || state.user_object.auth?.is_superuser || state.user_object.auth?.as_admin,
        my_company: state => state.user_object.auth?.company,
        my_holding: state => state.user_object.auth?.company?.holding,
        companies_in_my_holding: state => {
            if (!state.user_object.auth?.company?.holding) return [];
            const holdingId = state.user_object.auth.company.holding.uuid;
            return state.companies.filter(company => 
                company.holding && company.holding.uuid === holdingId
            );
        },
        is_external_customer: state => state.user_object.auth?.is_external_customer,
        is_external_workshop: state => state.user_object.auth?.is_external_workshop,
    },
    actions: {
        setTokens(data) {
            this.access_token = data.access
            this.refresh_token = data.refresh
            this.authenticated = true
            localStorage.setItem('access_token', this.access_token)
            localStorage.setItem('refresh_token', this.refresh_token)
        },
        refreshToken(at) {
            this.access_token = at
            localStorage.setItem('access_token', this.access_token)
        },
        cleanSession() {
            this.access_token = ""
            this.refresh_token = ""
            this.authenticated = false
            localStorage.removeItem('access_token')
            localStorage.removeItem('refresh_token')
            console.log("[Auth] Session cleaned from localStorage")
        },
        resetStore() {
            // Limpiar datos de usuario
            this.user_object = {
                _flag: "not-initialized",
                username: "",
                auth: {}
            }
            // Limpiar datos de compañías, usuarios y holdings
            this.companies = []
            this.users = []
            this.holdings = []
            console.log("[Auth] Store data reset")
        },
        
        async logout() {
            console.log("[Auth] Starting logout process")
            
            try {
                // Try to logout from backend first
                await backend_api.post('/auth/logout/')
                console.log("[Auth] Backend logout successful")
            } catch (error) {
                console.warn("[Auth] Backend logout failed, proceeding with local cleanup:", error.message)
                
                // If backend logout fails due to CSRF/CORS issues, continue with local cleanup
                if (error.response?.status === 403) {
                    console.log("[Auth] CSRF/CORS error during logout, cleaning session locally")
                }
            }
            
            // Always clean local session regardless of backend response
            this.cleanSession()
            this.resetStore()
            
            // Clear any app-level state
            const app_store = useAppStore()
            app_store.resetStore()
            
            console.log("[Auth] Logout process completed")
            
            // Emit success notification
            bus.emit("notification", {
                timeout: 3000,
                text: "Logged out successfully",
                color: "info"
            })
            
            return Promise.resolve()
        },
        setUserObject(data) {
            this.user_object = data
        },

        login(data) {
            const app_store = useAppStore()
            // Reset all stores before loading new user data
            this.resetStore()
            app_store.resetStore()
            useRefurbishmentStore().resetStore()
            useShipmentsStore().resetStore()
            usePartnumbersStore().resetStore()
            useExternalShipmentsStore().resetStore()
            useContainersStore().resetStore()
            return backend_api.post('/my_user/token/', data)
                .then(({ data }) => {
                    this.setTokens(data)
                    app_store.refreshBaseUserData(true)
                    app_store.initialLoad()
                })
        },
        refresh() {
            const app_store = useAppStore()
            console.log("[i] Refreshing access token")
            if (this.refresh_token == "") bus.emit("events/session/logout")
            return backend_api.post('/my_user/token/refresh/', { refresh: this.refresh_token })
                .then(({ data }) => {
                    this.refreshToken(data.access)
                    app_store.refreshBaseUserData(true)
                })
                .catch(() => {
                    bus.emit("notification", {
                        timeout: 8000,
                        text: "Failed to refresh session. You have to login again.",
                        color: "error"
                    });
                })
        },
        getUserObject() {
            return backend_api.get('/my_user/')
                .then(({ data }) => {
                    this.setUserObject(data)
                })
                .catch(e => console.log(`Cant get User Object: ${e}`))
        },


        // Sync mutations
        setCompanies: base.actions.setter("companies"),
        createCompany: base.actions.creator("companies"),
        updateCompany: base.actions.updater("companies"),
        removeCompany: base.actions.remover("companies"),
        // Async backend actions
        loadCompanies: base.actions.loader("companies/", "Companies"),
        postCompany: base.actions.poster("companies/", "Company"),
        putCompany: base.actions.putter("companies/", "Company"),
        deleteCompany: base.actions.deleter("companies/", "Company"),

        // Sync mutations
        setUsers: base.actions.setter("users"),
        createUser: base.actions.creator("users"),
        updateUser: base.actions.updater("users"),
        removeUser: base.actions.remover("users"),
        // Async backend actions
        loadUsers: base.actions.loader("users/", "Users"),
        postUser: base.actions.poster("users/", "User"),
        putUser: base.actions.putter("users/", "User"),
        deleteUser: base.actions.deleter("users/", "User"),
        
        // Sync mutations for holdings
        setHoldings: base.actions.setter("holdings"),
        createHolding: base.actions.creator("holdings"),
        updateHolding: base.actions.updater("holdings"),
        removeHolding: base.actions.remover("holdings"),
        // Async backend actions for holdings
        loadHoldings: base.actions.loader("holdings/", "Holdings"),
        postHolding: base.actions.poster("holdings/", "Holding"),
        putHolding: base.actions.putter("holdings/", "Holding"),
        deleteHolding: base.actions.deleter("holdings/", "Holding"),
        postUserPassword(payload) {
            return backend_api.put('users/' + payload.id + "/change_password/", payload)
                .then(({ data }) =>
                    bus.emit("notification", {
                        timeout: 3000,
                        text: data.detail,
                        color: "success",
                    })
                )
                .catch(e => bus.emit("notification", {
                    timeout: 8000,
                    text: e,
                    color: "error",
                }))
        },
    },
})
